import { getActivePromotions, type Product, PROMOTION_TYPES, SearchModes } from '@/domainLogic/index.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import {
  getTranslationServer,
  type TranslationDictionary,
} from '@/app/components/translations/createTranslationDictionary.ts';
import { getActivePeriodicPromotion } from '@/app/common/periodicPromotions.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import type { Localization } from '../../common/Localization';

export const calculateNights = (from?: string, to?: string): number => {
  if (!from || !to) return 0;
  const date1 = new Date(from);
  const date2 = new Date(to);
  const DifferenceInTime = date2.getTime() - date1.getTime();
  const DifferenceInDays = DifferenceInTime / (1000 * 3600 * 24);

  return DifferenceInDays;
};

export const decorateInventoryAge = (minutes: number, language: string): { unit: string; time: string } => {
  const isDE = language.toUpperCase() === 'DE';
  let unit: string = isDE ? 'Min.' : 'min';
  let time = `${minutes}`;
  if (minutes > 60) {
    unit = 'h';
    time = `${Math.floor(minutes / 60)}`;
  }
  return { unit, time };
};

export const getAccomodationType = (
  accomondationTypeId: number,
  translationDictionary: TranslationDictionary
): string | null => {
  switch (accomondationTypeId) {
    case 1:
      return getTranslationServer('vacation.search.filter.accommodation.type.hotels', translationDictionary);
    case 2:
      return getTranslationServer('vacation.search.filter.accommodation.type.resorts', translationDictionary);
    case 3:
      return getTranslationServer('vacation.search.filter.accommodation.type.apartments', translationDictionary);
    case 4:
      return getTranslationServer('vacation.search.filter.accommodation.type.hostels', translationDictionary);
    case 5:
      return getTranslationServer('vacation.search.filter.accommodation.type.motels', translationDictionary);
    case 6:
      return getTranslationServer('vacation.search.filter.accommodation.type.countryhouses', translationDictionary);
    case 7:
      return getTranslationServer('vacation.search.filter.accommodation.type.guesthouses', translationDictionary);
    case 8:
      return getTranslationServer('vacation.search.filter.accommodation.type.bedandbreakfasts', translationDictionary);
    case 9:
      return getTranslationServer('vacation.search.filter.accommodation.type.villas', translationDictionary);
    case 10:
      return getTranslationServer('vacation.search.filter.accommodation.type.boatsandcruises', translationDictionary);
    case 11:
      return getTranslationServer('vacation.search.filter.accommodation.type.campsites', translationDictionary);
    case 12:
      return getTranslationServer('vacation.search.filter.accommodation.type.riads', translationDictionary);
    case 13:
      return getTranslationServer('vacation.search.filter.accommodation.type.ryokans', translationDictionary);
    case 14:
      return getTranslationServer('vacation.search.filter.accommodation.type.aparthotels', translationDictionary);
    default:
      return null;
  }
};

export const getProductLabel = ({
  searchMode,
  translationDictionary,
}: {
  searchMode: string;
  translationDictionary: TranslationDictionary;
}) => {
  switch (searchMode) {
    case SearchModes.HO:
      return getTranslationServer('vacation.oss.components.searchmode.ho', translationDictionary).toUpperCase();
    case SearchModes.FF:
      return getTranslationServer('vacation.oss.components.searchmode.ff', translationDictionary).toUpperCase();
    case SearchModes.TH:
      return getTranslationServer('vacation.oss.components.searchmode.th', translationDictionary).toUpperCase();
    case SearchModes.DP:
    default:
      return getTranslationServer('vacation.oss.components.searchmode.dp', translationDictionary).toUpperCase();
  }
};

export const getPriceLabel = ({
  t,
  isPerPerson,
  // hasMultipleRooms,
  // isHoOneNightStay,
}: {
  t: TranslationFunction;
  isMobileOrOverlay: boolean;
  isPerPerson: boolean;
  isDesktop: boolean;
  hasMultipleRooms: boolean;
  isHoOneNightStay: boolean;
}) => {
  if (isPerPerson) return t('vacation.oss.cards.pp');
  // if (hasMultipleRooms) return t('vacation.oss.cards.per_room_night');
  // if (isHoOneNightStay) return t('vacation.oss.cards.total');
  return '';
};

export const getPromotions = (
  enabledPromotions: PROMOTION_TYPES[],
  product: Product,
  localization: Localization,
  commonData: CommonData
): PROMOTION_TYPES[] => {
  const promotionTypes = getActivePromotions(enabledPromotions, product);
  const forceJanuarySales = localization?.configValuesApp?.forceJanuarySales;
  const utcTime = commonData.serverTime;
  const promotions: PROMOTION_TYPES[] = [];

  promotionTypes.forEach((promo) => {
    if (promo === PROMOTION_TYPES.FLASH_SALES) {
      if (forceJanuarySales) {
        promotions.push(PROMOTION_TYPES.JANUARY_SALE);
        return;
      }
      const periodicPromotion = getActivePeriodicPromotion(utcTime, localization);
      if (periodicPromotion) {
        promotions.push(PROMOTION_TYPES.FLASH_SALES, periodicPromotion);
        return;
      }
    }
    promotions.push(promo);
  });

  return promotions;
};
